// URL helpers: encode/decode Settings as query parameters, and build shareable
// links. Routing is hash-based (`#/test`, `#/share?id=…`) so the static Go SPA
// fallback never needs per-route server config; settings params live in the
// hash query so a copied link restores the form on load.

import type { Settings } from '@/engine/types';
import { parseIntList } from './format';

/** Split the current location hash into a path and its query params. */
export function parseHash(hash: string): { path: string; params: URLSearchParams } {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const [path, query = ''] = raw.split('?');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return { path: normalized === '/' ? '/test' : normalized, params: new URLSearchParams(query) };
}

/** Encode settings into URL query parameters. */
export function settingsToParams(settings: Settings): URLSearchParams {
  const p = new URLSearchParams();
  p.set('sizes', settings.sizes.join('.'));
  p.set('pps', String(settings.pps));
  p.set('dur', String(settings.durationMs));
  p.set('delay', String(settings.acceptableDelayMs));
  p.set('warmup', String(settings.warmupMs));
  if (settings.ipMode !== 'auto') p.set('ip', settings.ipMode);
  return p;
}

function num(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Merge any settings params present in `params` onto the given defaults. */
export function paramsToSettings(params: URLSearchParams, defaults: Settings): Settings {
  const sizesRaw = params.get('sizes');
  const sizes = sizesRaw ? parseIntList(sizesRaw.replace(/\./g, ',')) : defaults.sizes;
  return {
    sizes: sizes.length > 0 ? sizes : defaults.sizes,
    pps: num(params, 'pps', defaults.pps),
    durationMs: num(params, 'dur', defaults.durationMs),
    acceptableDelayMs: num(params, 'delay', defaults.acceptableDelayMs),
    warmupMs: num(params, 'warmup', defaults.warmupMs),
    ipMode: ((): Settings['ipMode'] => {
      const v = params.get('ip');
      return v === 'ipv4' || v === 'ipv6' || v === 'auto' ? v : defaults.ipMode;
    })(),
  };
}

/** Whether the given params carry any settings fields at all. */
export function hasSettingsParams(params: URLSearchParams): boolean {
  return ['sizes', 'pps', 'dur', 'delay', 'warmup'].some((k) => params.has(k));
}

/** Absolute link that opens the Test page pre-filled with the given settings. */
export function settingsLink(settings: Settings): string {
  const params = settingsToParams(settings);
  return `${window.location.origin}/#/test?${params.toString()}`;
}

/** Absolute link that opens the read-only Share page for a stored result. */
export function shareLink(shareId: string): string {
  return `${window.location.origin}/#/share?id=${encodeURIComponent(shareId)}`;
}

/** Copy text to the clipboard, returning whether it succeeded. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}
